import type { FileUIPart } from 'ai'
import type {
  EmailImportPart,
  EmailImportSource,
} from '@/lib/email-conversion/email-import-part'
import {
  decodeEmailSourceDataUrl,
  getEmailSourceFormatForPart,
} from '@/lib/email-conversion/source-formats/email-source-data-url'
import type { EmailSourceFormat } from '@/lib/email-conversion/source-formats/types'
import { isRedactedInlineAttachmentUrl } from '@/lib/shared/chat-attachments/redacted-inline-attachment'
import type { SubmittedMessageFile } from '@/lib/shared/chat-message-builders'

export const CODE_ATTACHMENT_SUMMARY_PREFIX = 'Attachment upload:'
export const HTML_PASTE_MIN_LENGTH = 220
export const CODE_PASTE_MIN_LENGTH = 140

const HTML_START_MARKERS = [
  '<!doctype',
  '<html',
  '<head',
  '<body',
  '<table',
  '<section',
  '<article',
  '<header',
  '<footer',
  '<main',
  '<style',
  '<meta',
  '<title',
]

const HTML_EMAIL_TAGS = [
  'table',
  'tr',
  'td',
  'tbody',
  'img',
  'a',
  'p',
  'div',
  'section',
  'span',
  'center',
  'style',
  'meta',
  'title',
]

const MJML_TAGS = [
  'mjml',
  'mj-head',
  'mj-body',
  'mj-title',
  'mj-section',
  'mj-column',
  'mj-text',
  'mj-button',
  'mj-image',
  'mj-wrapper',
  'mj-spacer',
]

const REACT_EMAIL_COMPONENTS = [
  'Html',
  'Head',
  'Preview',
  'Body',
  'Container',
  'Section',
  'Row',
  'Column',
  'Text',
  'Button',
  'Img',
]

type HtmlLikeFilePart = {
  filename?: FileUIPart['filename']
  mediaType?: FileUIPart['mediaType']
  url?: FileUIPart['url']
  previewText?: string
}

type UserMessagePart =
  | {
      type?: 'file'
      url?: string
      filename?: string
      mediaType?: string
    }
  | {
      type?: 'text'
      text?: string
    }
  | {
      type?: string
      [key: string]: unknown
    }

export type UserMessageLike = {
  role?: string
  parts?: Array<UserMessagePart>
  content?: Array<UserMessagePart>
}

function isUserMessageFilePart(
  part: UserMessagePart | undefined
): part is Extract<UserMessagePart, { type?: 'file' }> {
  return part?.type === 'file'
}

function isUserMessageTextPart(
  part: UserMessagePart | undefined
): part is Extract<UserMessagePart, { type?: 'text' }> & { text: string } {
  return part?.type === 'text' && typeof part.text === 'string'
}

export type UploadedSourceFile = {
  syntax: 'html' | 'mjml' | 'jsx' | 'tsx' | 'eml'
  /** Hosted http(s) URL only. Absent for inline attachments. */
  url?: string
  filename?: string
  mediaType?: string
  /** Decoded inline source — never printed into a prompt. */
  inline?: { format: EmailSourceFormat; text: string }
}

export type UploadedSourceContext = {
  file?: UploadedSourceFile
}

function normalizePastedSource(text: string): string {
  return text.replace(/\r\n/g, '\n').trim()
}

const FOLLOW_UP_MAX_CHARS = 8000

/**
 * Same bar as `extractHtmlCandidate`'s no-marker fallback, plus MJML.
 * A prose-embedded candidate must clear this so tag mentions in ordinary
 * questions never become imports.
 */
export function hasImportableMarkupSubstance(candidate: string): boolean {
  const tags = candidate.match(/<\/?[a-z][\w:-]*\b[^>]*>/gi) ?? []
  if (tags.length < 8) {
    return false
  }
  const closingTags = candidate.match(/<\/[a-z][\w:-]*>/gi) ?? []
  if (closingTags.length < 3) {
    return false
  }
  const lower = candidate.toLowerCase()
  return (
    HTML_EMAIL_TAGS.some((tag) => lower.includes(`<${tag}`)) ||
    MJML_TAGS.some((tag) => lower.includes(`<${tag}`))
  )
}

const VOID_MARKUP_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
  'mj-divider',
  'mj-image',
  'mj-spacer',
])

const MARKUP_TAG_RE = /<\/?([a-z][\w:-]*)\b[^>]*>/gi

const DOCUMENT_ROOT_TAGS = new Set(['html', 'mjml'])
const STRUCTURAL_MARKUP_TAGS = new Set([
  'article',
  'body',
  'footer',
  'header',
  'html',
  'main',
  'mj-body',
  'mj-section',
  'mj-wrapper',
  'mjml',
  'section',
  'table',
])
const CONTINUATION_MARKUP_TAGS = new Set(['head', 'style', 'title'])
const LAYOUT_CONTINUATION_TAGS = new Set([
  'body',
  'html',
  'mj-body',
  'mj-section',
  'mj-wrapper',
  'mjml',
  'table',
])

/**
 * Letters between a finished close and the next open mean the following
 * tags are instruction, not more of the document.
 */
function hasInstructionProse(gap: string): boolean {
  return /[A-Za-z]/.test(gap.replace(/<[^>]*>/g, ''))
}

/**
 * Last balanced document close. Extra closes and later balanced
 * instruction literals (`replace <p>old</p>`, `<html>…</html>`,
 * `wrap it in <section>`) do not extend the source. Every empty-stack
 * close moves the boundary, including `</style>` / `</p>`, so a
 * style-led ESP export and a stacked-paragraph email keep their
 * body. Sibling tables or sibling wrappers with only whitespace
 * between them still both import. A different structural tag after a
 * structural close (`</table>` then `<section>`), or a non-layout
 * structural tag after any other close (`</div>` / `</p>` / `</tr>`
 * then `<section>`), is instruction even when the gap has no
 * letters. Layout continuation (`</p>` / `</div>` / `</tr>` then
 * `<table>`, `</style>` then `<html>`) stays one email. Style, head,
 * and title closes do not count as soft closes, so a style-led
 * `<section>` body still imports.
 */
function findLastBalancedMarkupEnd(candidate: string): number | undefined {
  const stack: Array<string> = []
  let lastBalancedEnd: number | undefined
  let lastClosedStructuralTag: string | undefined
  let hasClosedSoftTag = false
  let hasClosedDocumentRoot = false
  let hasFrozenBoundary = false
  for (const match of candidate.matchAll(MARKUP_TAG_RE)) {
    const name = match[1]?.toLowerCase()
    if (!name || match.index === undefined) {
      continue
    }
    const isClose = match[0].startsWith('</')
    if (!isClose) {
      if (VOID_MARKUP_TAGS.has(name) || /\/\s*>$/.test(match[0])) {
        continue
      }
      if (
        stack.length === 0 &&
        lastBalancedEnd !== undefined &&
        !hasClosedDocumentRoot &&
        !hasFrozenBoundary
      ) {
        const gap = candidate.slice(lastBalancedEnd, match.index)
        const isDifferentStructuralSibling =
          lastClosedStructuralTag !== undefined &&
          STRUCTURAL_MARKUP_TAGS.has(name) &&
          name !== lastClosedStructuralTag
        const isForeignStructuralAfterSoftClose =
          hasClosedSoftTag &&
          STRUCTURAL_MARKUP_TAGS.has(name) &&
          !LAYOUT_CONTINUATION_TAGS.has(name)
        if (
          hasInstructionProse(gap) ||
          isDifferentStructuralSibling ||
          isForeignStructuralAfterSoftClose
        ) {
          hasFrozenBoundary = true
        }
      }
      stack.push(name)
      continue
    }
    const openIndex = stack.lastIndexOf(name)
    if (openIndex < 0) {
      continue
    }
    stack.length = openIndex
    if (stack.length > 0) {
      continue
    }
    if (hasClosedDocumentRoot || hasFrozenBoundary) {
      continue
    }
    lastBalancedEnd = match.index + match[0].length
    if (DOCUMENT_ROOT_TAGS.has(name)) {
      lastClosedStructuralTag = name
      hasClosedSoftTag = false
      hasClosedDocumentRoot = true
      continue
    }
    if (STRUCTURAL_MARKUP_TAGS.has(name)) {
      lastClosedStructuralTag = name
      hasClosedSoftTag = false
      continue
    }
    if (CONTINUATION_MARKUP_TAGS.has(name)) {
      continue
    }
    lastClosedStructuralTag = undefined
    hasClosedSoftTag = true
  }
  return lastBalancedEnd
}

const DOCUMENT_ROOT_START_RE = /<(?:!doctype\s+html|html|mjml)\b/gi

function extractLastDocumentRootSlice(text: string): string | undefined {
  let lastIndex = -1
  let lastKind: 'doctype' | 'html' | 'mjml' | undefined
  for (const match of text.matchAll(DOCUMENT_ROOT_START_RE)) {
    if (match.index === undefined) {
      continue
    }
    const token = match[0].toLowerCase()
    let kind: 'doctype' | 'html' | 'mjml'
    if (token.startsWith('<!doctype')) {
      kind = 'doctype'
    } else if (token.startsWith('<mjml')) {
      kind = 'mjml'
    } else {
      kind = 'html'
    }
    if (
      kind === 'html' &&
      lastKind === 'doctype' &&
      lastIndex >= 0 &&
      !hasInstructionProse(text.slice(lastIndex, match.index))
    ) {
      continue
    }
    lastIndex = match.index
    lastKind = kind
  }
  if (lastIndex < 0) {
    return
  }
  return text.slice(lastIndex).trim()
}

function isolateFromCandidate(
  rawCandidate: string,
  submitted: string
): { markup: string; followUpText?: string } | null {
  const markupEnd = findLastBalancedMarkupEnd(rawCandidate)
  if (markupEnd === undefined) {
    return null
  }
  const markup = rawCandidate.slice(0, markupEnd).trim()
  if (!hasImportableMarkupSubstance(markup)) {
    return null
  }

  const after = rawCandidate.slice(markupEnd).trim()
  const markupAt = submitted.indexOf(markup)
  const before = markupAt > 0 ? submitted.slice(0, markupAt).trim() : ''
  const followUpText = [before, after].filter(Boolean).join('\n\n')
  if (!followUpText) {
    return { markup }
  }
  if (followUpText.length > FOLLOW_UP_MAX_CHARS) {
    return null
  }
  return { markup, followUpText }
}

const FIRST_MARKUP_RE = /<(?:!doctype\b|[a-z][\w:-]*\b)/i

function sliceFromFirstMarkup(text: string): string {
  const firstTagAt = text.search(FIRST_MARKUP_RE)
  return firstTagAt > 0 ? text.slice(firstTagAt) : text
}

function hasMarkupBeforeCandidate(
  submitted: string,
  rawCandidate: string
): boolean {
  const candidateAt = submitted.indexOf(rawCandidate)
  if (candidateAt <= 0) {
    return false
  }
  return FIRST_MARKUP_RE.test(submitted.slice(0, candidateAt))
}

/**
 * End markup at the last balanced document close so a trailing
 * instruction stays out of the source. If a start-marker tag in the
 * instruction (`make the <header> darker`) captured the candidate
 * before `<html>` / `<mjml>`, retry from that later root. If a later
 * start marker stole a marker-free wrapper or paragraph document,
 * retry from the first tag — only when that prefix is itself markup,
 * so leading prose stays in `followUpText`.
 */
function isolateImportableMarkup(
  text: string
): { markup: string; followUpText?: string } | null {
  const normalized = normalizePastedSource(text)
  const fullFence = extractFullFence(normalized)
  const submitted = fullFence?.content ?? normalized
  const rawCandidate =
    extractMjmlCandidate(normalized) ?? extractHtmlCandidate(normalized)
  if (!rawCandidate) {
    return null
  }

  const fromCandidate = isolateFromCandidate(rawCandidate, submitted)
  const hasMarkupPrefix = hasMarkupBeforeCandidate(submitted, rawCandidate)
  if (fromCandidate && !hasMarkupPrefix) {
    return fromCandidate
  }

  if (!fromCandidate) {
    const laterRoot = extractLastDocumentRootSlice(submitted)
    if (laterRoot && laterRoot !== rawCandidate) {
      const fromRoot = isolateFromCandidate(laterRoot, submitted)
      if (fromRoot) {
        return fromRoot
      }
    }
  }

  if (rawCandidate !== submitted) {
    const fromSubmitted = isolateFromCandidate(
      sliceFromFirstMarkup(submitted),
      submitted
    )
    if (
      fromSubmitted &&
      (!fromCandidate ||
        (fromSubmitted.markup.length > fromCandidate.markup.length &&
          fromSubmitted.markup.includes(fromCandidate.markup)))
    ) {
      return fromSubmitted
    }
  }
  return fromCandidate
}

/**
 * Build the explicit deterministic-import signal used by the chat route.
 * - Whole-document paste, no files → import-only (no followUpText).
 * - Whole document + surrounding instruction, or single HTML/MJML upload
 *   + prompt → import + followUpText (chained).
 * - Tag mentions in ordinary questions stay null (document bar).
 * Figma URLs are never detected here.
 */
export function buildEmailImportDataPart(
  text: string,
  files: ReadonlyArray<SubmittedMessageFile>
): EmailImportPart | null {
  const normalizedText = normalizePastedSource(text)
  if (files.length === 0 && normalizedText) {
    const isolated = isolateImportableMarkup(normalizedText)
    if (!isolated) {
      return null
    }
    const source: EmailImportSource = {
      kind: 'html',
      html: isolated.markup,
    }
    if (!isolated.followUpText) {
      return { type: 'data-email-import', source }
    }
    return {
      type: 'data-email-import',
      source,
      followUpText: isolated.followUpText,
    }
  }

  if (files.length !== 1) {
    return null
  }
  const file = files[0]
  if (!file.url || isRedactedInlineAttachmentUrl(file.url)) {
    return null
  }
  if (normalizedText && isolateImportableMarkup(normalizedText)) {
    return null
  }
  let source: EmailImportSource
  const inline = decodeEmailSourceDataUrl(file.url)
  if (inline) {
    // A file the browser read inline: the content rides the import part, so
    // the route never fetches anything.
    source = {
      kind: 'file',
      format: getEmailSourceFormatForPart(file) ?? inline.format,
      text: inline.text,
      filename: file.filename ?? 'email-source',
    }
  } else if (isHtmlAttachmentPart(file) && /^https?:\/\//i.test(file.url)) {
    source = { kind: 'url', url: file.url }
  } else {
    return null
  }
  if (!normalizedText) {
    return { type: 'data-email-import', source }
  }
  if (normalizedText.length > FOLLOW_UP_MAX_CHARS) {
    return null
  }
  return {
    type: 'data-email-import',
    source,
    followUpText: normalizedText,
  }
}

function extractFullFence(
  text: string
): { language?: string; content: string } | null {
  const match = text.match(/^```([a-z0-9+#-]+)?\s*\n?([\s\S]*?)\n?```$/i)
  if (!match?.[2]) {
    return null
  }
  return {
    language: match[1]?.toLowerCase(),
    content: normalizePastedSource(match[2]),
  }
}

export function extractHtmlCandidate(text: string): string | undefined {
  const normalized = normalizePastedSource(text)
  if (!normalized) {
    return
  }

  const fullFence = extractFullFence(normalized)
  const candidateSource =
    fullFence?.language && ['html', 'xml', 'mjml'].includes(fullFence.language)
      ? fullFence.content
      : normalized

  if (
    looksLikeTsxSource(candidateSource) ||
    looksLikeJsxSource(candidateSource)
  ) {
    return
  }

  const lower = candidateSource.toLowerCase()
  const start = HTML_START_MARKERS.map((marker) => lower.indexOf(marker))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0]

  if (typeof start === 'number') {
    return candidateSource.slice(start).trim()
  }

  const tagMatches = candidateSource.match(/<\/?[a-z][\w:-]*\b[^>]*>/gi) ?? []
  const closingTagMatches = candidateSource.match(/<\/[a-z][\w:-]*>/gi) ?? []
  if (
    tagMatches.length >= 8 &&
    closingTagMatches.length >= 3 &&
    HTML_EMAIL_TAGS.some((tag) => lower.includes(`<${tag}`))
  ) {
    return candidateSource.trim()
  }
}

export function extractMjmlCandidate(text: string): string | undefined {
  const normalized = normalizePastedSource(text)
  if (!normalized) {
    return
  }

  const fullFence = extractFullFence(normalized)
  const candidateSource =
    fullFence?.language && ['mjml', 'xml', 'html'].includes(fullFence.language)
      ? fullFence.content
      : normalized
  const lower = candidateSource.toLowerCase()

  if (
    MJML_TAGS.some((tag) => lower.includes(`<${tag}`)) &&
    (candidateSource.match(/<\/mj-[a-z-]+>/gi) ?? []).length >= 1
  ) {
    return candidateSource.trim()
  }
}

export function looksLikeMjmlDocument(
  text: string,
  minLength = HTML_PASTE_MIN_LENGTH
): boolean {
  const candidate = extractMjmlCandidate(text)
  return Boolean(candidate && candidate.length >= minLength)
}

function containsJsxTags(text: string): boolean {
  return (
    /<([A-Z][\w.]*)\b/.test(text) ||
    /<>[\s\S]*<\/>/.test(text) ||
    REACT_EMAIL_COMPONENTS.some((tag) => text.includes(`<${tag}`))
  )
}

export function looksLikeTsxSource(
  text: string,
  minLength = CODE_PASTE_MIN_LENGTH
): boolean {
  const normalized =
    extractFullFence(normalizePastedSource(text))?.content ??
    normalizePastedSource(text)
  if (normalized.length < minLength) {
    return false
  }
  if (looksLikeMjmlDocument(normalized)) {
    return false
  }
  return (
    containsJsxTags(normalized) &&
    /\b(import|export|return|const|function|type|interface)\b/.test(
      normalized
    ) &&
    /:\s*[A-Z][\w<>[\]|& ,]*|\binterface\b|\btype\b|\bReact\./.test(normalized)
  )
}

export function looksLikeJsxSource(
  text: string,
  minLength = CODE_PASTE_MIN_LENGTH
): boolean {
  const normalized =
    extractFullFence(normalizePastedSource(text))?.content ??
    normalizePastedSource(text)
  if (normalized.length < minLength) {
    return false
  }
  if (looksLikeMjmlDocument(normalized)) {
    return false
  }
  return (
    containsJsxTags(normalized) &&
    /\b(import|export|return|const|function)\b/.test(normalized) &&
    !/\binterface\b|\btype\b|:\s*[A-Z][\w<>[\]|& ,]*/.test(normalized)
  )
}

export function isGeneratedAttachmentSummary(text: string): boolean {
  return text.trimStart().startsWith(CODE_ATTACHMENT_SUMMARY_PREFIX)
}

export function isHtmlAttachmentPart(part: {
  filename?: string
  mediaType?: string
  url?: string
}): boolean {
  const mediaType = part.mediaType?.toLowerCase() ?? ''
  const filename = part.filename?.toLowerCase() ?? ''

  return (
    mediaType.includes('text/html') ||
    mediaType.includes('application/xhtml+xml') ||
    mediaType.includes('xml') ||
    filename.endsWith('.html') ||
    filename.endsWith('.htm') ||
    filename.endsWith('.mjml') ||
    urlLooksLikeHtmlData(part.url)
  )
}

/** HTML/MJML/EML/React Email files — hosted or inline — the import can consume. */
export function isEmailSourceAttachmentPart(part: {
  filename?: string
  mediaType?: string
  url?: string
}): boolean {
  return (
    isHtmlAttachmentPart(part) ||
    getEmailSourceFormatForPart(part) !== undefined
  )
}

export function isCodeAttachmentPart(part: {
  filename?: string
  mediaType?: string
  url?: string
}): boolean {
  const mediaType = part.mediaType?.toLowerCase() ?? ''
  const filename = part.filename?.toLowerCase() ?? ''

  return (
    isEmailSourceAttachmentPart(part) ||
    filename.endsWith('.tsx') ||
    filename.endsWith('.jsx') ||
    filename.endsWith('.ts') ||
    filename.endsWith('.js') ||
    filename.endsWith('.css') ||
    filename.endsWith('.json') ||
    mediaType.includes('javascript') ||
    mediaType.includes('typescript') ||
    mediaType.includes('text/plain') ||
    mediaType.includes('json') ||
    mediaType.includes('css')
  )
}

function urlLooksLikeHtmlData(url?: string): boolean {
  if (!url) {
    return false
  }
  return /^data:text\/html(?:;|,)/i.test(url)
}

type HtmlAttachmentReadOptions = {
  fetchImpl?: typeof fetch
  allowRemoteUrl?: (url: string) => boolean | Promise<boolean>
}

export async function readHtmlAttachmentContent(
  part: HtmlLikeFilePart,
  fetchOrOptions: typeof fetch | HtmlAttachmentReadOptions = fetch
): Promise<string | undefined> {
  if (!isEmailSourceAttachmentPart(part)) {
    return
  }
  const fetchImpl =
    typeof fetchOrOptions === 'function'
      ? fetchOrOptions
      : (fetchOrOptions.fetchImpl ?? fetch)
  const allowRemoteUrl =
    typeof fetchOrOptions === 'function'
      ? undefined
      : fetchOrOptions.allowRemoteUrl

  if (!part.url || isRedactedInlineAttachmentUrl(part.url)) {
    return
  }
  const inline = decodeEmailSourceDataUrl(part.url)
  if (inline) {
    // Markup keeps the html-candidate trim; a message or module is returned
    // verbatim for the shared normalizer.
    return inline.format === 'html' || inline.format === 'mjml'
      ? (extractHtmlCandidate(inline.text) ?? inline.text.trim())
      : inline.text
  }
  if (allowRemoteUrl && !(await allowRemoteUrl(part.url))) {
    return
  }

  try {
    const response = await fetchImpl(part.url)
    if (!response.ok) {
      return
    }
    const html = await response.text()
    return extractHtmlCandidate(html) ?? html.trim()
  } catch {}
}

// NOTE: pasted-content file detection (detectPastedCodeAttachment and its
// looksLike* code heuristics) was deliberately removed. It converted pasted
// chat text into file attachments and false-positived on plain-prose prompts
// (e.g. "world class" + "CTA: Ready" read as TypeScript). Pasted text must
// always stay text; only files the user explicitly attaches are uploads.

export async function extractLatestUserHtmlFromMessages(
  messages: Array<UserMessageLike>,
  fetchImpl: typeof fetch = fetch,
  allowRemoteUrl?: (url: string) => boolean | Promise<boolean>
): Promise<string | undefined> {
  const readLatestHtmlInParts = async (
    parts: Array<UserMessagePart>,
    index: number
  ): Promise<string | undefined> => {
    if (index < 0) {
      return
    }
    const part = parts[index]
    if (isUserMessageFilePart(part) && typeof part.url === 'string') {
      const html = await readHtmlAttachmentContent(part, {
        fetchImpl,
        allowRemoteUrl,
      })
      if (html?.trim()) {
        return html.trim()
      }
    }
    return readLatestHtmlInParts(parts, index - 1)
  }

  const searchMessages = async (index: number): Promise<string | undefined> => {
    if (index < 0) {
      return
    }

    const message = messages[index]
    if (message?.role !== 'user') {
      return searchMessages(index - 1)
    }

    const parts =
      message.parts ?? (Array.isArray(message.content) ? message.content : [])
    if (!Array.isArray(parts)) {
      return searchMessages(index - 1)
    }

    const html = await readLatestHtmlInParts(parts, parts.length - 1)
    if (html?.trim()) {
      return html.trim()
    }

    const text = parts
      .filter(isUserMessageTextPart)
      .map((part) => part.text)
      .join('\n')
    const candidate = extractHtmlCandidate(text)
    if (candidate?.trim()) {
      return candidate.trim()
    }

    return searchMessages(index - 1)
  }

  return searchMessages(messages.length - 1)
}

type LatestUserMarkupFile = {
  url: string
  filename?: string
  mediaType?: string
  /** Present when the file rode the message inline (never fetched). */
  inline?: { format: EmailSourceFormat; text: string }
}

export function extractLatestUserMarkupFile(
  messages: Array<UserMessageLike>
): LatestUserMarkupFile | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message?.role !== 'user') {
      continue
    }

    const parts =
      message.parts ?? (Array.isArray(message.content) ? message.content : [])
    if (!Array.isArray(parts)) {
      continue
    }

    for (let j = parts.length - 1; j >= 0; j -= 1) {
      const part = parts[j]
      if (
        isUserMessageFilePart(part) &&
        typeof part.url === 'string' &&
        !isRedactedInlineAttachmentUrl(part.url) &&
        isEmailSourceAttachmentPart(part)
      ) {
        const inline = decodeEmailSourceDataUrl(part.url)
        return {
          url: part.url,
          ...(typeof part.filename === 'string'
            ? { filename: part.filename }
            : {}),
          ...(typeof part.mediaType === 'string'
            ? { mediaType: part.mediaType }
            : {}),
          ...(inline ? { inline } : {}),
        }
      }
    }
  }
}

function detectUploadedSourceSyntax(part: {
  filename?: string
  mediaType?: string
  url?: string
}): UploadedSourceFile['syntax'] | undefined {
  if (urlLooksLikeHtmlData(part.url)) {
    return 'html'
  }
  const format = getEmailSourceFormatForPart(part)
  if (!format) {
    return
  }
  if (format === 'react-email') {
    return (part.filename ?? '').toLowerCase().endsWith('.tsx') ? 'tsx' : 'jsx'
  }
  return format
}

export function extractLatestUserSourceFile(
  messages: Array<UserMessageLike>
): UploadedSourceFile | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message?.role !== 'user') {
      continue
    }

    const parts =
      message.parts ?? (Array.isArray(message.content) ? message.content : [])
    if (!Array.isArray(parts)) {
      continue
    }

    for (let j = parts.length - 1; j >= 0; j -= 1) {
      const part = parts[j]
      if (!isUserMessageFilePart(part) || typeof part.url !== 'string') {
        continue
      }

      if (isRedactedInlineAttachmentUrl(part.url)) {
        continue
      }
      const syntax = detectUploadedSourceSyntax(part)
      if (!syntax) {
        continue
      }

      const inline = decodeEmailSourceDataUrl(part.url)
      return {
        syntax,
        ...(inline ? { inline } : { url: part.url }),
        ...(typeof part.filename === 'string'
          ? { filename: part.filename }
          : {}),
        ...(typeof part.mediaType === 'string'
          ? { mediaType: part.mediaType }
          : {}),
      }
    }
  }
}

/**
 * Preserve an uploaded source as model context for mixed AI requests without
 * guessing intent from prose. Import-only uploads use `data-email-import` and
 * are removed before this path; everything left here stays a normal toolset.
 */
export function getUploadedSourceContext(args: {
  messages: Array<UserMessageLike>
}): UploadedSourceContext {
  const file = extractLatestUserSourceFile(args.messages)
  return file ? { file } : {}
}
